import { AudioLinesIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTitle } from 'react-use';

import { FilterToolbar } from '@/components/organisms/FilterToolbar/FilterToolbar';
import { Button } from '@/components/ui/button';
import { EmptySkeleton } from '@/components/ui/custom/EmptySkeleton';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { useEmptyStateView } from '@/hooks/useEmptyStateView';
import useMembers from '@/hooks/useMembers';
import { useReportEmptyState } from '@/hooks/useReportEmptyState';
import { useSidebar } from '@/hooks/useSidebar';
import useSpeechModels from '@/modules/developerTools/hooks/useSpeechModels';

import { SPEECH_PROVIDER_NAMES } from './helper';
import { SpeechModelItem } from './SpeechModelItem';
import {
  CREATOR_FILTER,
  PROVIDER_FILTER,
  creatorFilterDefinition,
  providerFilterDefinition,
} from '../../components/ResourceList/filterDefinitions';
import { ResourceList } from '../../components/ResourceList/ResourceList';
import { byLastUsed, useResourceList } from '../../hooks/useResourceList';
import { SpeechModel } from '../../types';

interface SpeechModelsProps {
  onEmptyStateChange: (isEmpty: boolean) => void;
}

export const SpeechModels = ({ onEmptyStateChange }: SpeechModelsProps) => {
  const { t } = useTranslation();
  const { speechModels, listStatus, refetchList, providersStatus, refetchProviders, canWrite } =
    useSpeechModels();
  const { getMember } = useMembers();
  const { meta, isOpen, open } = useSidebar();
  const selectedId = isOpen('speech_model') ? meta?.speechModelId : undefined;

  const openPanel = (speechModel?: SpeechModel) => {
    open('speech_model', speechModel ? { speechModelId: speechModel.speech_model_id } : undefined);
  };

  const { visibleItems, toolbarProps } = useResourceList(speechModels, {
    storageKey: 'speechModelsFilters',
    preferencesKey: 'speech_models',
    filterValues: {
      [PROVIDER_FILTER]: (model) => model.provider,
      [CREATOR_FILTER]: (model) => model.created_by,
    },
    searchText: (model) => [model.name, SPEECH_PROVIDER_NAMES[model.provider]],
    sort: byLastUsed,
  });

  const filterDefinitions = [
    providerFilterDefinition(
      speechModels,
      (provider) => SPEECH_PROVIDER_NAMES[provider] ?? provider,
      t
    ),
    creatorFilterDefinition(speechModels, getMember, t),
  ];

  useTitle(t('pages.devtool_speech_models'));

  const isLoading = listStatus === 'pending';
  // Create needs the provider catalog, so its failure blocks the page like the list's
  const isError = listStatus === 'error' || providersStatus === 'error';
  const { showEmpty, showEmptySkeleton, showList, isEmptyPage } = useEmptyStateView({
    scope: 'speech-models',
    isLoading,
    isEmpty: !speechModels.length,
    isError,
  });

  // An empty page hides the header's Create button, so a failed load cannot offer it either
  useReportEmptyState(isEmptyPage || isError, onEmptyStateChange);

  if (isError) {
    return (
      <div
        role="alert"
        className="flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/5 p-3"
      >
        <span className="text-sm leading-5 text-destructive">
          {t('developer_tools.speech_models.load_failed')}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            if (listStatus === 'error') {
              refetchList();
            }
            if (providersStatus === 'error') {
              refetchProviders();
            }
          }}
        >
          {t('common.try_again')}
        </Button>
      </div>
    );
  }

  return (
    <div className="pt-5 pb-20">
      {showEmpty && (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <AudioLinesIcon />
            </EmptyMedia>
            <EmptyTitle>{t('developer_tools.speech_models.empty_title')}</EmptyTitle>
            <EmptyDescription>
              {t('developer_tools.speech_models.empty_description')}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button size="sm" onClick={() => openPanel()} disabled={!canWrite}>
              <PlusIcon size={16} />
              {t('common.create')}
            </Button>
          </EmptyContent>
        </Empty>
      )}

      {showEmptySkeleton && <EmptySkeleton descriptionLines={2} />}

      {showList && (
        <>
          <FilterToolbar
            {...toolbarProps}
            searchPlaceholder={t('developer_tools.speech_models.search')}
            filterDefinitions={filterDefinitions}
          />

          <ResourceList
            isLoading={isLoading}
            isEmpty={visibleItems.length === 0}
            noResultsMessage={t('developer_tools.speech_models.no_results')}
          >
            {visibleItems.map((speechModel) => (
              <SpeechModelItem
                key={speechModel.speech_model_id}
                speechModel={speechModel}
                isSelected={speechModel.speech_model_id === selectedId}
                onOpen={openPanel}
              />
            ))}
          </ResourceList>
        </>
      )}
    </div>
  );
};
